/**
 * Namespaced password-gate persistence — the single contract every UI gate unlocks through.
 *
 * A gate is a fat-finger deterrent, NOT an auth boundary: role/org checks on the relay
 * remain the real access control. What this module owns is *where the unlocked bit lives*
 * and *how long it survives*. Ten views hand-rolled the same
 * `sessionStorage.setItem('<x>_unlocked', 'true')` / `getItem(...) === 'true'` pair — with
 * the key string duplicated between each gate and its sibling `index.tsx`. #337 moves the
 * four research gates onto this contract; the rest are listed below as deliberately unmigrated.
 *
 * A namespace is an explicit key descriptor rather than an id we derive keys from. That is
 * deliberate: every migrated gate already had a shipped key in real users' browsers, so
 * naming the keys outright preserves each one by construction instead of by convention, and
 * a future gate can be added without inventing a key-derivation scheme that has to stay
 * compatible with the ones that came before.
 *
 * Capabilities are opt-in per namespace, because the gates genuinely differ:
 *   - `hashKey` present  -> the unlock is bound to a secret hash, so rotating the password
 *                           invalidates already-unlocked tabs.
 *   - `hashKey` absent   -> a plain unlocked/locked bit. This is what the research gates
 *                           have always done; binding a hash now would silently log open
 *                           tabs out on the next rotation.
 *   - `rememberKey` present -> `remember: true` persists past the tab, with a 7-day TTL.
 *   - `rememberKey` absent  -> session-only; `remember: true` is ignored, not half-honored.
 *
 * ## Gates NOT on this contract (as of #337)
 *
 * The first migration covers the research-only cluster. These operational gates still
 * hand-roll their own persistence, deliberately:
 *
 * | Gate                                        | Key                   | Why it was left |
 * | ------------------------------------------- | --------------------- | --------------- |
 * | `views/VanillaMM/VanillaGate.tsx`           | `vanilla_unlocked`    | Arms live quoting on the entity account, and is shared by VanillaMM and Marty. #337 non-goal: no live-money gate migration without review. |
 * | `views/CashLedgerPreview/CashLedgerGate.tsx`| `cash_ledger_unlocked`| Operational accounting surface with its own `aria-label`/layout assertions; out of the research-only first cluster. |
 *
 * Both compare an `import.meta.env.VITE_*_GATE_PASSWORD` plaintext value. This module
 * introduces no plaintext secret and does not change theirs.
 */

import { storageKey } from './storageChannel';

/** How long a remembered unlock outlives the tab. */
const REMEMBER_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Where one gate's unlocked bit lives. Every key is a literal shipped key — see the module
 * comment for why these are spelled out rather than derived.
 */
export interface GateNamespace {
  /** sessionStorage key holding `'true'` while this gate is unlocked in this tab. */
  readonly sessionKey: string;
  /** sessionStorage key binding the unlock to a secret hash. Omit for an unbound gate. */
  readonly hashKey?: string;
  /** localStorage key for the TTL'd remember payload. Omit for a session-only gate. */
  readonly rememberKey?: string;
}

interface RememberPayload {
  hash: string;
  expiresAt: number;
}

/** Options for {@link recordUnlock}. Both are ignored by namespaces lacking the capability. */
export interface UnlockOptions {
  /** Hash of the accepted secret. Stored only when the namespace has a `hashKey`. */
  hash?: string;
  /** Persist past this tab. Honored only when the namespace has a `rememberKey`. */
  remember?: boolean;
}

/**
 * SHA-256 of `message` as lowercase hex — the hash every gate compares its input against.
 *
 * Lives beside the namespaces because a gate needs exactly these two things and nothing
 * else; four views previously carried a byte-identical private copy of this function.
 */
export async function sha256(message: string): Promise<string> {
  const data = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Whether this gate is already unlocked — from the session, or from a live remember-me.
 *
 * `expectedHash` is required (and compared) only by hash-bound namespaces; unbound ones
 * ignore it. A remember-me hit re-hydrates the session so subsequent checks stay cheap, and
 * an expired or mismatched payload is deleted on the way out rather than left to rot.
 */
export function isUnlocked(ns: GateNamespace, expectedHash?: string): boolean {
  const hashKey = ns.hashKey;
  if (hashKey !== undefined && !expectedHash) return false;

  const sessionMatches =
    sessionStorage.getItem(ns.sessionKey) === 'true' &&
    (hashKey === undefined || sessionStorage.getItem(hashKey) === expectedHash);
  if (sessionMatches) return true;

  if (!ns.rememberKey) return false;

  try {
    const key = storageKey(ns.rememberKey);
    const raw = localStorage.getItem(key);
    if (raw) {
      const payload: RememberPayload = JSON.parse(raw);
      const hashMatches = hashKey === undefined || payload.hash === expectedHash;
      if (hashMatches && payload.expiresAt > Date.now()) {
        writeSession(ns, expectedHash);
        return true;
      }
      localStorage.removeItem(key);
    }
  } catch {
    // Malformed payload — treat as locked and move on.
  }

  return false;
}

/**
 * Mark this gate unlocked. Writes the remember payload only when the caller asked for it AND
 * the namespace supports it.
 *
 * A hash-bound namespace given no hash persists NOTHING: the session bit alone can never
 * satisfy `isUnlocked` for such a namespace, so writing it would leave storage in a state
 * that reads as locked forever — half-honoring a request this contract can't fulfil.
 */
export function recordUnlock(ns: GateNamespace, opts: UnlockOptions = {}): void {
  if (ns.hashKey !== undefined && opts.hash === undefined) return;

  writeSession(ns, opts.hash);

  if (opts.remember && ns.rememberKey) {
    const payload: RememberPayload = {
      hash: opts.hash ?? '',
      expiresAt: Date.now() + REMEMBER_TTL_MS,
    };
    localStorage.setItem(storageKey(ns.rememberKey), JSON.stringify(payload));
  }
}

function writeSession(ns: GateNamespace, hash: string | undefined): void {
  sessionStorage.setItem(ns.sessionKey, 'true');
  if (ns.hashKey !== undefined && hash !== undefined) {
    sessionStorage.setItem(ns.hashKey, hash);
  }
}
